import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import type { VoiceSearchFilters } from './types'

interface VoiceFiltersProps {
  filters: VoiceSearchFilters
  onFiltersChange: (filters: Partial<VoiceSearchFilters>) => void
  onClear: () => void
  showDropdowns: boolean
  debounceMs?: number
}

const GENDER_OPTIONS = ['', 'male', 'female']
const AGE_OPTIONS = ['', 'young', 'middle_aged', 'old']
const LANGUAGE_OPTIONS = ['', 'en', 'es', 'fr', 'de', 'pt', 'it', 'ja', 'ko', 'zh', 'ar', 'hi', 'pl', 'nl', 'sv']
const ACCENT_OPTIONS = ['', 'american', 'british', 'australian', 'indian', 'mexican', 'chilean', 'spanish', 'french', 'german']

export function VoiceFilters({
  filters,
  onFiltersChange,
  onClear,
  showDropdowns,
  debounceMs = 300,
}: VoiceFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search ?? '')

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ search: searchInput || undefined })
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [searchInput, debounceMs, onFiltersChange])

  const hasActiveFilters = !!(
    filters.search || filters.gender || filters.language || filters.age || filters.accent
  )

  return (
    <div className="flex flex-wrap gap-2 p-3 border-b border-border">
      {/* Search input */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search voices..."
          className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:border-primary"
        />
      </div>

      {/* Filter dropdowns (only on Voice Library tab) */}
      {showDropdowns && (
        <div className="flex gap-1.5">
          <FilterSelect
            value={filters.gender ?? ''}
            onChange={(v) => onFiltersChange({ gender: v || undefined })}
            options={GENDER_OPTIONS}
            label="Gender"
          />
          <FilterSelect
            value={filters.language ?? ''}
            onChange={(v) => onFiltersChange({ language: v || undefined })}
            options={LANGUAGE_OPTIONS}
            label="Language"
          />
          <FilterSelect
            value={filters.age ?? ''}
            onChange={(v) => onFiltersChange({ age: v || undefined })}
            options={AGE_OPTIONS}
            label="Age"
          />
          <FilterSelect
            value={filters.accent ?? ''}
            onChange={(v) => onFiltersChange({ accent: v || undefined })}
            options={ACCENT_OPTIONS}
            label="Accent"
          />
        </div>
      )}

      {/* Clear filters */}
      {hasActiveFilters && (
        <button
          onClick={() => {
            setSearchInput('')
            onClear()
          }}
          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
        >
          <X className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  )
}

function FilterSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string
  onChange: (value: string) => void
  options: string[]
  label: string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-background border border-border rounded-md px-2 py-2 text-xs text-foreground focus:outline-hidden focus:border-primary appearance-none cursor-pointer"
    >
      <option value="">{label}</option>
      {options
        .filter((o) => o !== '')
        .map((opt) => (
          <option key={opt} value={opt}>
            {opt.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          </option>
        ))}
    </select>
  )
}
